import data from "@emoji-mart/data";
import { init } from "emoji-mart";

init({ data });

function nativeForEmoji(emoji) {
  return emoji?.skins?.[0]?.native || null;
}

function buildMaps() {
  const shortcodes = new Map();
  const emoticons = new Map();

  const emojis = data.emojis || {};
  for (const id of Object.keys(emojis)) {
    const emoji = emojis[id];
    const native = nativeForEmoji(emoji);
    if (!native) continue;

    shortcodes.set(id.toLowerCase(), native);
    if (Array.isArray(emoji.emoticons)) {
      for (const emo of emoji.emoticons) {
        if (!emo || emoticons.has(emo)) continue;
        emoticons.set(emo, native);
      }
    }
  }

  const aliases = data.aliases || {};
  for (const alias of Object.keys(aliases)) {
    const id = aliases[alias];
    const native = shortcodes.get(String(id).toLowerCase());
    if (native) shortcodes.set(alias.toLowerCase(), native);
  }

  const emoticonList = [...emoticons.keys()].sort((a, b) => b.length - a.length);
  return { shortcodes, emoticons, emoticonList };
}

const { shortcodes, emoticons, emoticonList } = buildMaps();

const SHORTCODE_RE = /:([a-zA-Z0-9_+-]+):$/;

/**
 * Insert emoji into a controlled text value at the current caret (or append).
 */
export function insertAtCursor(value, emoji, textarea) {
  const current = value || "";
  if (!textarea || typeof textarea.selectionStart !== "number") {
    return { next: current + emoji, caret: (current + emoji).length };
  }
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const next = current.slice(0, start) + emoji + current.slice(end);
  return { next, caret: start + emoji.length };
}

function isUrlishBefore(before, emoticon) {
  // Avoid turning https:// into https😕
  if (!emoticon.includes("/")) return false;
  const idx = before.length - emoticon.length;
  const prefix = before.slice(0, idx);
  return /https?:$/i.test(prefix) || /https?:\/$/i.test(prefix);
}

/**
 * Expand a completed :shortcode: or emoticon ending at caret.
 * @returns {{ next: string, caret: number, changed: boolean }}
 */
export function expandEmojiShortcuts(text, caret) {
  const value = text || "";
  const pos =
    typeof caret === "number" && Number.isFinite(caret)
      ? Math.max(0, Math.min(caret, value.length))
      : value.length;
  const before = value.slice(0, pos);
  const after = value.slice(pos);

  const short = before.match(SHORTCODE_RE);
  if (short) {
    const name = short[1].toLowerCase();
    const native = shortcodes.get(name);
    if (native) {
      const nextBefore = before.slice(0, before.length - short[0].length) + native;
      return {
        next: nextBefore + after,
        caret: nextBefore.length,
        changed: true,
      };
    }
  }

  for (const emo of emoticonList) {
    if (!before.endsWith(emo)) continue;
    if (isUrlishBefore(before, emo)) continue;
    const native = emoticons.get(emo);
    if (!native) continue;
    const nextBefore = before.slice(0, before.length - emo.length) + native;
    return {
      next: nextBefore + after,
      caret: nextBefore.length,
      changed: true,
    };
  }

  return { next: value, caret: pos, changed: false };
}

/**
 * Use as a textarea onChange handler. Calls onChange(nextText) and restores caret.
 */
export function onEmojiAwareInputChange(e, onChange) {
  const el = e?.target;
  if (!el) return;
  const raw = el.value;
  const caret =
    typeof el.selectionStart === "number" ? el.selectionStart : raw.length;
  const { next, caret: nextCaret, changed } = expandEmojiShortcuts(raw, caret);
  onChange?.(next);
  if (changed) {
    requestAnimationFrame(() => {
      try {
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
      } catch {
        /* ignore */
      }
    });
  }
}
